<template>
    <!-- Award Alerts Section -->
    <div class="py-12 md:py-20 px-4 md:px-8">
        <div class="max-w-5xl mx-auto">
            <div class="text-center text-2xl md:text-4xl leading-tight">
                <p class="text-slate-800 dark:text-slate-100">The alerts that</p>
                <h2 class="font-medium text-orange">
                    make membership worth it - many times over.
                </h2>
                <p
                    class="text-slate-500 dark:text-slate-400 text-base mt-4 max-w-2xl mx-auto leading-relaxed"
                >
                    The best award space disappears fast. 10xTravel+ members
                    get three types of alerts to make sure you're always
                    first in line.
                </p>
            </div>

            <div
                class="flex flex-col lg:flex-row items-center gap-12 mt-12"
            >
                <!-- Alert Types List -->
                <ul class="flex-1 space-y-6">
                    <li
                        v-for="alertType in alertTypes"
                        :key="alertType.title"
                        class="flex items-start gap-3"
                    >
                        <span
                            class="w-6 h-6 flex items-center justify-start shrink-0"
                        >
                            <Icon
                                :icon="alertType.icon"
                                class="w-6 h-6 text-orange fill-orange"
                            />
                        </span>
                        <div class="flex flex-col gap-1">
                            <h4
                                class="text-base font-medium text-slate-800 dark:text-slate-100"
                            >
                                {{ alertType.title }}
                            </h4>
                            <p class="text-base text-slate-500 dark:text-slate-400">
                                {{ alertType.description }}
                            </p>
                        </div>
                    </li>
                </ul>

                <!-- Alert Cards Illustration -->
                <div
                    class="hidden lg:block relative w-[472px] h-[379px] shrink-0"
                >
                    <!-- Background gradient blobs -->
                    <div
                        class="absolute inset-0"
                        style="
                            -webkit-mask-image: radial-gradient(
                                ellipse 90% 85% at center,
                                black 20%,
                                rgba(0, 0, 0, 0.6) 40%,
                                rgba(0, 0, 0, 0.3) 60%,
                                transparent 80%
                            );
                            mask-image: radial-gradient(
                                ellipse 90% 85% at center,
                                black 20%,
                                rgba(0, 0, 0, 0.6) 40%,
                                rgba(0, 0, 0, 0.3) 60%,
                                transparent 80%
                            );
                        "
                    >
                        <div
                            class="absolute w-[370px] h-[205px] left-[26px] top-[103px] rotate-[11deg] opacity-70 bg-orange-200 rounded-full blur-[60px]"
                        ></div>
                        <div
                            class="absolute w-[267px] h-[150px] left-[120px] top-[155px] -rotate-[20deg] opacity-40 bg-orange-500 rounded-full blur-[60px]"
                        ></div>
                    </div>

                    <!-- Stacked Alert Cards -->
                    <div class="absolute left-[67px] top-[24px] w-[337px]">
                        <!-- Featured Card (tilted) -->
                        <article
                            class="absolute left-[5px] top-[84px] w-[316px] p-4 bg-white dark:bg-slate-800 rounded-xl shadow-lg ring-1 ring-orange-300 -rotate-[4deg] origin-top-left"
                        >
                            <div class="flex items-start gap-3">
                                <span
                                    class="p-2.5 bg-orange-50 rounded-full shrink-0"
                                >
                                    <Icon
                                        icon="mail"
                                        class="w-4 h-4 text-orange"
                                    />
                                </span>
                                <div class="flex-1 min-w-0">
                                    <h5
                                        class="text-xs font-medium text-slate-800 dark:text-slate-100"
                                    >
                                        ANA The Room → Tokyo
                                    </h5>
                                    <p
                                        class="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5"
                                    >
                                        Business Class The Room on NH109
                                        JFK→NRT is open for 2 passengers in
                                        March.
                                    </p>
                                    <div
                                        class="flex items-center gap-2 mt-2.5 text-[8px]"
                                    >
                                        <div>
                                            <span class="text-slate-500 dark:text-slate-400">
                                                Miles
                                            </span>
                                            <p class="text-slate-800 dark:text-slate-100">
                                                88K
                                            </p>
                                        </div>
                                        <div>
                                            <span class="text-slate-500 dark:text-slate-400">
                                                Cash
                                            </span>
                                            <p class="text-slate-800 dark:text-slate-100">
                                                $86
                                            </p>
                                        </div>
                                        <div>
                                            <span class="text-slate-500 dark:text-slate-400">
                                                Start date
                                            </span>
                                            <p class="text-slate-800 dark:text-slate-100">
                                                March, 12
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        class="mt-2 px-2.5 py-1 bg-orange text-white text-[9px] font-medium rounded flex items-center gap-1"
                                    >
                                        Book now
                                        <Icon
                                            icon="chevron-right"
                                            class="w-3 h-3"
                                        />
                                    </button>
                                </div>
                            </div>
                        </article>

                        <!-- Background cards (faded) -->
                        <article
                            class="absolute left-[27px] top-[33px] w-[284px] p-2.5 bg-white dark:bg-slate-800 rounded-lg shadow-sm ring-1 ring-black/5 opacity-50"
                        >
                            <div class="flex items-start gap-2.5">
                                <span
                                    class="p-2 bg-orange-50 rounded-full shrink-0"
                                >
                                    <Icon
                                        icon="bell"
                                        class="w-3.5 h-3.5 text-orange"
                                    />
                                </span>
                                <div class="flex-1">
                                    <p
                                        class="text-[11px] font-medium text-slate-800 dark:text-slate-100 opacity-70"
                                    >
                                        Qsuites → Doha
                                    </p>
                                    <p
                                        class="text-[10px] text-slate-500 dark:text-slate-400 opacity-70"
                                    >
                                        March wide open · 70K miles · Book
                                        now
                                    </p>
                                </div>
                            </div>
                        </article>

                        <article
                            class="absolute left-[27px] top-[213px] w-[284px] p-2.5 bg-white dark:bg-slate-800 rounded-lg shadow-sm ring-1 ring-black/5 opacity-80"
                        >
                            <div class="flex items-start gap-2.5">
                                <span
                                    class="p-2 bg-orange-50 rounded-full shrink-0"
                                >
                                    <Icon
                                        icon="mail"
                                        class="w-3.5 h-3.5 text-orange"
                                    />
                                </span>
                                <div class="flex-1">
                                    <p
                                        class="text-[11px] font-medium text-slate-800 dark:text-slate-100 opacity-80"
                                    >
                                        Lufthansa First → Frankfurt
                                    </p>
                                    <p
                                        class="text-[10px] text-slate-500 dark:text-slate-400 opacity-80"
                                    >
                                        New Year's Eve · 85K miles · 2
                                        passengers
                                    </p>
                                </div>
                            </div>
                        </article>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import Icon from '@/components/ui/icon/icon.vue';

const alertTypes = [
    {
        icon: 'luxury',
        title: 'Unicorn Alerts',
        description:
            'The "drop everything and book this" alerts - rare premium cabins like JAL First, ANA, The Room, and Qatar QSuites, or coveted routes during popular times of year.',
    },
    {
        icon: 'airplane',
        title: 'Flight Deals',
        description:
            "Patterns of strong award availability to a destination, so you can plan a trip to somewhere you've been eyeing.",
    },
    {
        icon: 'mail',
        title: 'Weekly Roundups',
        description:
            'A weekly scan of award availability between the US and the world - a different region every day.',
    },
];
</script>
